import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import tradingService from '../services/tradingService';
import {isCanceledRequest} from '../services/api';
import Header from '../components/Header';
import {isTruthy} from '../utils/boolean';
import './StrategyScreenerPage.css';

const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '—';

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 4,
    ...options,
  }).format(number);
};

const formatUsdt = (value) => {
    if (value === null || value === undefined || value === '') return '—';

    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);

    return `${number >= 0 ? '+' : ''}${formatNumber(number)} USDT`;
};

const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '—';

    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);

    return `${number >= 0 ? '+' : ''}${formatNumber(number, {maximumFractionDigits: 2})}%`;
};

const formatProfitFactor = (strategy) => {
  const performance = strategy?.performance ?? {};
  const rawValue = performance?.profit_factor;
  const value = Number(rawValue);

  if (performance?.profit_factor_is_infinite) {
    return '∞';
  }

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return '—';
  }

  if (!Number.isFinite(value)) {
    return String(rawValue);
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value);
};

const getProfitFactorValue = (strategy) => {
  const performance = strategy?.performance ?? {};
  if (performance?.profit_factor_is_infinite) {
    return Number.POSITIVE_INFINITY;
  }

  const value = Number(performance?.profit_factor);
  return Number.isFinite(value) ? value : null;
};

const formatDateTime = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const sortOptions = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'profit_factor', label: 'Best profit factor' },
  { value: 'trades', label: 'Most trades' },
  { value: 'subscriptions', label: 'Most subscribers' },
  { value: 'recent', label: 'Most recent' },
  { value: 'updated', label: 'Recently updated' },
];

export default function StrategyScreenerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [exchange, setExchange] = useState('all');
  const [minProfitFactor, setMinProfitFactor] = useState('');
  const [sortBy, setSortBy] = useState('recommended');

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.strategies.list({
          all_strategies: true,
          signal: ac.signal,
        });

        setStrategies(res?.data?.strategies ?? []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setStrategies([]);
        setError(err?.message || 'Failed to load strategies');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [refreshNonce]);

  const exchangeOptions = useMemo(() => {
    const values = new Map();

    for (const strategy of strategies) {
      const exchangeName = String(strategy?.account?.exchange?.exchange_name ?? '').trim();
      if (!exchangeName) continue;
      values.set(exchangeName, exchangeName);
    }

    return [...values.values()].sort((a, b) => a.localeCompare(b));
  }, [strategies]);

  const screenerStats = useMemo(() => {
    const active = strategies.filter((strategy) => isTruthy(strategy?.is_active)).length;
    const totalSubscribers = strategies.reduce((sum, strategy) => sum + Number(strategy?.subscriptions?.total || 0), 0);
    const activeSubscribers = strategies.reduce((sum, strategy) => sum + Number(strategy?.subscriptions?.active || 0), 0);
    const totalTrades = strategies.reduce((sum, strategy) => sum + Number(strategy?.performance?.total_trades || 0), 0);

    return {
      total: strategies.length,
      active,
      inactive: Math.max(strategies.length - active, 0),
      totalSubscribers,
      activeSubscribers,
      totalTrades,
    };
  }, [strategies]);

  const filteredStrategies = useMemo(() => {
    const q = query.trim().toLowerCase();

    const scored = strategies.filter((strategy) => {
      const active = isTruthy(strategy?.is_active);
      if (status === 'active' && !active) return false;
      if (status === 'inactive' && active) return false;

      const exchangeName = String(strategy?.account?.exchange?.exchange_name ?? '').trim();
      if (exchange !== 'all' && exchangeName !== exchange) return false;

      const profitFactor = getProfitFactorValue(strategy);
      const minPf = minProfitFactor.trim() === '' ? null : Number(minProfitFactor);
      if (Number.isFinite(minPf) && (profitFactor === null || profitFactor < minPf)) {
        return false;
      }

      if (!q) return true;

      const haystack = [
        strategy?.label,
        strategy?.name,
        strategy?.description,
        strategy?.short_id,
        strategy?.account?.label,
        strategy?.account?.number,
        strategy?.account?.exchange?.exchange_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });

    const getSortValue = (strategy) => {
      const trades = Number(strategy?.performance?.total_trades || 0);
      const subscribers = Number(strategy?.subscriptions?.total || 0);
      const activeSubscribers = Number(strategy?.subscriptions?.active || 0);
      const lastTrade = new Date(strategy?.performance?.last_trade_date || 0).getTime() || 0;
      const updated = new Date(strategy?.activity?.date_updated || 0).getTime() || 0;
      const activeBoost = isTruthy(strategy?.is_active) ? 1 : 0;
      const profitFactor = getProfitFactorValue(strategy);
      const profitFactorSort = profitFactor === null ? Number.NEGATIVE_INFINITY : profitFactor;

      return {
        recommended: activeBoost * 1_000_000_000 + subscribers * 10_000 + trades * 100 + activeSubscribers,
        profit_factor: profitFactorSort,
        trades: trades * 10_000 + subscribers,
        subscriptions: subscribers * 10_000 + activeSubscribers * 100 + trades,
        recent: lastTrade,
        updated,
      };
    };

    return [...scored].sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);

      const sortKey = sortBy === 'recommended' ? 'recommended' : sortBy;
      if (sortKey === 'profit_factor') {
        const avValue = av[sortKey];
        const bvValue = bv[sortKey];
        if (avValue === bvValue) return 0;
        return bvValue > avValue ? 1 : -1;
      }

      return (bv[sortKey] || 0) - (av[sortKey] || 0);
    });
  }, [strategies, query, status, exchange, minProfitFactor, sortBy]);

  return (
    <>
      <Header />
      <main className="container">
        <div className="card screener-page">
        <div className="screener-page__header">
          <div>
            <h1 className="screener-page__title">Strategy Screener</h1>
            <p className="screener-page__lead">
              Scan public strategies across all users, compare activity, and open the ones worth inspecting.
            </p>
          </div>

          <button
            type="button"
            className="screener-page__btn screener-page__btn--primary"
            onClick={() => setRefreshNonce((n) => n + 1)}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        <div className="screener-page__stats" aria-label="Screener summary">
          <div className="screener-page__stat">
            <span>Total</span>
            <strong>{formatNumber(screenerStats.total)}</strong>
          </div>
          <div className="screener-page__stat">
            <span>Active</span>
            <strong>{formatNumber(screenerStats.active)}</strong>
          </div>
          <div className="screener-page__stat">
            <span>Subscribers</span>
            <strong>{formatNumber(screenerStats.totalSubscribers)}</strong>
          </div>
          <div className="screener-page__stat">
            <span>Trades</span>
            <strong>{formatNumber(screenerStats.totalTrades)}</strong>
          </div>
        </div>

        <div className="screener-page__filters">
          <label className="screener-page__field">
            <span>Search</span>
            <input
              className="screener-page__control"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, short id, account, exchange"
            />
          </label>

          <label className="screener-page__field">
            <span>Status</span>
            <select className="screener-page__control" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>

          <label className="screener-page__field">
            <span>Exchange</span>
            <select className="screener-page__control" value={exchange} onChange={(e) => setExchange(e.target.value)}>
              <option value="all">All</option>
              {exchangeOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="screener-page__field">
            <span>Min profit factor</span>
            <input
              className="screener-page__control"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={minProfitFactor}
              onChange={(e) => setMinProfitFactor(e.target.value)}
              placeholder="e.g. 1.5"
            />
          </label>

          <label className="screener-page__field">
            <span>Sort</span>
            <select className="screener-page__control" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <p className="screener-page__error">{error}</p> : null}
        {loading ? <p className="screener-page__loading">Loading strategies…</p> : null}

        {!loading ? (
          <div className="screener-page__grid">
            {filteredStrategies.length === 0 ? (
              <div className="screener-page__empty">
                <div className="screener-page__empty-title">No strategies match your filters</div>
                <div className="screener-page__empty-subtitle">
                  Try clearing the search box or switching to a different status.
                </div>
              </div>
            ) : (
              filteredStrategies.map((strategy) => {
                const active = isTruthy(strategy?.is_active);
                const isPublic = isTruthy(strategy?.is_public);
                const label = strategy?.label ?? strategy?.name ?? '—';
                const shortId = strategy?.short_id ?? '—';
                const description = strategy?.description ?? '—';
                const exchangeName = strategy?.account?.exchange?.exchange_name ?? '—';
                const trades = formatNumber(strategy?.performance?.total_trades);
                const executedTrades = formatNumber(strategy?.performance?.executed_trades);
                const profitFactor = formatProfitFactor(strategy);
                  const totalReturnUsdt = strategy?.performance?.total_return_usdt ?? strategy?.performance?.total_change_usdt;
                  const totalReturnPercent = strategy?.performance?.total_return_percent ?? strategy?.performance?.total_change_percent;
                  const returnClass = Number(totalReturnUsdt) > 0
                      ? 'screener-page__change screener-page__change--positive'
                      : Number(totalReturnUsdt) < 0
                          ? 'screener-page__change screener-page__change--negative'
                          : 'screener-page__change';
                const subscribers = formatNumber(strategy?.subscriptions?.total);
                const activeSubscribers = formatNumber(strategy?.subscriptions?.active);
                const lastTrade = formatDateTime(strategy?.performance?.last_trade_date);
                const updated = formatDateTime(strategy?.activity?.date_updated);

                return (
                    <article className="screener-page__card" key={strategy?.id ?? strategy?.short_id ?? 'strategy'}>
                    <div className="screener-page__card-top">
                      <div className="screener-page__meta">
                        <div className="screener-page__name" title={label}>
                          {label}
                        </div>
                      </div>

                      <span className={active ? 'screener-page__status screener-page__status--active' : 'screener-page__status screener-page__status--inactive'}>
                        {active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={isPublic ? 'screener-page__status screener-page__status--public' : 'screener-page__status screener-page__status--inactive'}>
                        {isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>

                    <div className="screener-page__description" title={description}>
                      {description}
                    </div>

                    <div className="screener-page__grid-kv">
                      <div className="screener-page__kv">
                        <span>Exchange</span>
                        <strong>{exchangeName}</strong>
                      </div>
                      <div className="screener-page__kv">
                        <span>Subscribers</span>
                        <strong>
                          {activeSubscribers} active · {subscribers} total
                        </strong>
                      </div>
                      <div className="screener-page__kv">
                        <span>Trades</span>
                        <strong>
                          {trades} total · {executedTrades} executed
                        </strong>
                      </div>
                      <div className="screener-page__kv">
                        <span>Profit factor</span>
                        <strong>{profitFactor}</strong>
                      </div>
                        <div className="screener-page__kv">
                            <span>Total return</span>
                            <strong className={returnClass}>
                                {formatUsdt(totalReturnUsdt)} · {formatPercent(totalReturnPercent)}
                            </strong>
                      </div>
                      <div className="screener-page__kv">
                        <span>Last trade</span>
                        <strong>{lastTrade}</strong>
                      </div>
                      <div className="screener-page__kv">
                        <span>Updated</span>
                        <strong>{updated}</strong>
                      </div>
                    </div>

                    <div className="screener-page__actions">
                      <button
                        type="button"
                        className="screener-page__btn screener-page__btn--secondary"
                        onClick={() => navigate(`/strategies/${encodeURIComponent(shortId)}`)}
                        disabled={!strategy?.short_id}
                      >
                        Open details
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        ) : null}
        </div>
      </main>
    </>
  );
}
